// A passage a post quotes, fetched from the ESV as the post is read. Exactly
// the verses named — `/api/quote` asks the ESV for those and no more — and
// built the way the chapter page builds its scripture: verse spans, tidied
// markers, glossary words, the reader's own text settings.
import { chapterOf, parseRef, toOsis, type PassageView } from "@bibleunleashed/shared";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef } from "react";
import { markTerms } from "@/glossary/markTerms";
import { api } from "@/lib/api";
import { useTermMatcher } from "@/lib/glossary";
import { useSettings } from "@/lib/settings";
import { scriptureClass } from "@/reader/scriptureClass";
import { dropNoteLists, tidyMarkers, wrapVerses } from "@/reader/verses";

function useQuote(osis: string) {
  return useQuery({
    queryKey: ["quote", osis],
    queryFn: () => api<PassageView>(`/api/quote/${encodeURIComponent(osis)}`),
    staleTime: 24 * 3_600_000,
    gcTime: 24 * 3_600_000,
  });
}

export function Quotation({ osis, display }: { osis: string; display: string }) {
  const { settings } = useSettings();
  const terms = useTermMatcher();
  const body = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useQuote(osis);

  useLayoutEffect(() => {
    const el = body.current;
    if (!el || !data) return;
    el.innerHTML = data.html;
    // The chapter it opens in, for any words the ESV sends before a verse marker.
    wrapVerses(el, toOsis(chapterOf(parseRef(osis))));
    tidyMarkers(el);
    dropNoteLists(el);
    markTerms(el, terms);
  }, [data, osis, terms]);

  return (
    <figure className="my-8 border-l-2 border-rule pl-5">
      <figcaption className="mb-2 font-sans text-[11px] tracking-[0.08em] text-ink-3 uppercase">{display} · ESV</figcaption>
      {isLoading && <p className="font-serif text-ink-3">Loading the passage…</p>}
      {error && (
        <p className="font-serif text-[14px] text-ink-2">
          Couldn’t load {display}: {error.message}
        </p>
      )}
      <div ref={body} className={scriptureClass(settings)} style={{ "--size-step": settings.size } as React.CSSProperties} />
    </figure>
  );
}
